import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  generateMnemonic,
  mnemonicBytesToSeed,
  deriveGonkaPrivateKey,
  derivePublicKey,
  pubkeyToAddress,
  deriveKeyFromPin,
  encrypt,
  generateNonce,
  zeroize,
} from '@gonka-wallet/crypto';
import {
  openWalletDb,
  saveWallet,
  type EncryptedWalletBlob,
} from '@gonka-wallet/storage';
import { Button } from '@gonka-wallet/ui-tokens';
import { MnemonicGrid } from '../components/MnemonicGrid.js';
import { PinPad } from '../components/PinPad.js';
import { BiometricOfferModal } from '../components/BiometricOfferModal.js';
import { isWebAuthnAvailable } from '../lib/webauthn.js';
import { useWalletStore } from '../stores/walletStore.js';
import { useToastStore } from '../stores/toastStore.js';
import { bytesToHex } from '../lib/format.js';
import { copySecretWithAutoClear } from '../lib/clipboard.js';

type Step = 'show' | 'verify' | 'pin' | 'confirm-pin';

function CreateNav({ step, onBack, total = 4, t }: { step: number; onBack: () => void; total?: number; t: (key: string) => string }) {
  return (
    <>
      <div className="flex items-center justify-between py-2">
        <button type="button" onClick={onBack} className="text-link text-[15px]">
          ‹ {t('common.back')}
        </button>
        <div className="text-[16px] font-semibold text-text-primary">{t('welcome.createNew')}</div>
        <div className="text-[11px] text-text-tertiary">{step} of {total}</div>
      </div>
      <div className="flex justify-center gap-1.5">
        {Array.from({ length: total }, (_, i) => (
          <div
            key={i}
            className={`h-0.5 w-7 rounded-full ${
              i === step - 1 ? 'bg-text-primary' : 'bg-surface-3'
            }`}
          />
        ))}
      </div>
    </>
  );
}

export function Create() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const addWallet = useWalletStore((s) => s.addWallet);
  const pushToast = useToastStore((s) => s.push);
  const [step, setStep] = useState<Step>('show');
  // 256-bit entropy → 24-word BIP39 mnemonic. Cosmos SDK supports
  // both 12 and 24, and 24 is the modern default for higher security
  // (full 256-bit seed).
  const wallets = useWalletStore((s) => s.wallets);
  const [mnemonic] = useState(() => generateMnemonic(256));
  const [acknowledged, setAcknowledged] = useState(false);
  const [walletName, setWalletName] = useState(() => t('create.defaultWalletName'));
  const [copied, setCopied] = useState(false);
  const [collected, setCollected] = useState<string[]>([]);
  const [firstPin, setFirstPin] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // After the wallet is created we may show the biometric offer
  // before navigating to /home. Hold the freshly created blob and
  // its derived encryption key here so the offer modal can wrap the
  // key without making the user re-type their PIN.
  const [pendingOffer, setPendingOffer] = useState<{
    wallet: EncryptedWalletBlob;
    derivedKey: Uint8Array;
  } | null>(null);

  async function copyMnemonic() {
    try {
      await copySecretWithAutoClear(mnemonic);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      pushToast(t('common.copyFailed'), 'error');
    }
  }

  const mnemonicWords = mnemonic.split(' ');

  // Verify flow: user must correctly pick 3 random words from the mnemonic
  // to prove they saved it. Positions are picked once per mnemonic so they
  // stay stable across re-renders.
  const verifyPositions = useMemo(() => {
    const indices = Array.from({ length: mnemonicWords.length }, (_, i) => i);
    // Fisher-Yates shuffle, take first 3, then sort ascending for nicer UX
    for (let i = indices.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [indices[i], indices[j]] = [indices[j]!, indices[i]!];
    }
    return indices.slice(0, 3).sort((a, b) => a - b);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mnemonic]);

  // Pool: 3 correct words + 9 distractors from the remaining mnemonic words,
  // shuffled. Keeps the pool at exactly 12 choices for a clean 3-col grid.
  const verifyPool = useMemo(() => {
    const correctSet = new Set(verifyPositions.map((p) => mnemonicWords[p]!));
    const distractors = mnemonicWords.filter((_w, i) => !verifyPositions.includes(i));
    // Shuffle distractors and take 9
    const shuffledDistractors = [...distractors].sort(() => Math.random() - 0.5).slice(0, 9);
    const all = [...Array.from(correctSet), ...shuffledDistractors];
    return all.sort(() => Math.random() - 0.5);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mnemonic, verifyPositions]);

  // collected[i] is the user's pick for verifyPositions[i] (or empty string
  // if not filled yet). Re-uses the existing `collected` state.
  const verifyCorrect =
    collected.length === verifyPositions.length &&
    collected.every((w, i) => w === mnemonicWords[verifyPositions[i]!]);
  const hasError = collected.some(
    (w, i) => i < collected.length && w !== mnemonicWords[verifyPositions[i]!],
  );
  const nextSlotIndex = collected.length;

  function pickWord(word: string) {
    if (hasError) return;
    if (nextSlotIndex >= verifyPositions.length) return;
    setCollected((c) => [...c, word]);
  }
  function resetVerify() {
    setCollected([]);
  }

  async function finalize(pin: string) {
    setError(null);
    let seed: Uint8Array = new Uint8Array(0);
    let priv: Uint8Array = new Uint8Array(0);
    let key: Uint8Array = new Uint8Array(0);
    let plaintext: Uint8Array = new Uint8Array(0);
    let keepKey = false;
    try {
      // Encode mnemonic to bytes once and use mnemonicBytesToSeed so we
      // don't take an extra JS-string copy of the seed material path.
      plaintext = new TextEncoder().encode(mnemonic);
      seed = await mnemonicBytesToSeed(plaintext);
      priv = deriveGonkaPrivateKey(seed);
      const pub = derivePublicKey(priv);
      const address = pubkeyToAddress(pub);

      const duplicate = wallets.some((w) => w.addresses.includes(address));
      if (duplicate) {
        setError(t('create.duplicateWallet'));
        return;
      }

      const salt = crypto.getRandomValues(new Uint8Array(16));
      key = await deriveKeyFromPin(pin, salt);
      const nonce = generateNonce();
      const ciphertext = encrypt(key, nonce, plaintext);

      const trimmedName = walletName.trim() || t('create.defaultWalletName');
      const blob: EncryptedWalletBlob = {
        version: 1,
        id: crypto.randomUUID(),
        name: trimmedName.slice(0, 64),
        kdf: 'argon2id',
        kdfParams: { m: 65536, t: 3, p: 4 },
        salt: bytesToHex(salt),
        nonce: bytesToHex(nonce),
        ciphertext: bytesToHex(ciphertext),
        hdPath: "m/44'/1200'/0'/0/0",
        addresses: [address],
        createdAt: new Date().toISOString(),
      };

      const db = await openWalletDb();
      await saveWallet(db, blob);
      addWallet(blob);

      // Offer biometric enrollment right here, before navigating, so
      // the user sees the prompt immediately after creating the
      // wallet and never has to type the PIN twice.
      if (isWebAuthnAvailable()) {
        // Hand off the derived key to the modal — keepKey prevents
        // the finally{} from zeroizing it out from under us. The
        // BiometricOfferModal owns it from here and zeroizes itself.
        const keyCopy = new Uint8Array(key);
        setPendingOffer({ wallet: blob, derivedKey: keyCopy });
        keepKey = true;
        return;
      }

      navigate('/home');
    } finally {
      if (keepKey) {
        zeroize(seed, priv, plaintext);
      } else {
        zeroize(seed, priv, key, plaintext);
      }
    }
  }

  function stepNumber(): number {
    switch (step) {
      case 'show': return 1;
      case 'verify': return 2;
      case 'pin': return 3;
      case 'confirm-pin': return 4;
    }
  }

  function handleBack() {
    switch (step) {
      case 'show': navigate(-1); break;
      case 'verify': setStep('show'); break;
      case 'pin': setStep('verify'); break;
      case 'confirm-pin':
        setStep('pin');
        setFirstPin(null);
        setError(null);
        break;
    }
  }

  if (step === 'show') {
    return (
      <div className="flex min-h-screen flex-col bg-bg px-5 pb-6 pt-3 text-text-primary">
        <CreateNav step={stepNumber()} onBack={handleBack} t={t} />
        <div className="mt-4 flex flex-1 flex-col gap-3.5">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[1.4px] text-text-tertiary">
              {t('create.recoveryPhrase')}
            </div>
            <div className="mt-1.5 text-[18px] font-semibold text-text-primary">
              {t('create.writeDownShort')}
            </div>
          </div>

          <div
            className="rounded-r-xl bg-surface-1 px-3.5 py-2.5"
            style={{ borderLeft: '3px solid var(--color-neg)' }}
          >
            <div className="text-[10px] font-semibold uppercase tracking-[1.4px] text-neg">
              {t('create.keepPrivate')}
            </div>
            <div className="mt-0.5 text-[11px] leading-snug text-text-secondary">
              {t('create.keepPrivateBody')}
            </div>
          </div>

          <label className="block text-[11px] text-text-secondary">
            {t('create.walletName')}
          </label>
          <input
            type="text"
            value={walletName}
            onChange={(e) => setWalletName(e.target.value)}
            placeholder={t('create.walletNamePlaceholder')}
            maxLength={64}
            className="rounded-xl bg-surface-1 p-3 text-text-primary"
          />

          <MnemonicGrid mnemonic={mnemonic} />

          <label className="flex items-center gap-3">
            <input
              type="checkbox"
              checked={acknowledged}
              onChange={(e) => setAcknowledged(e.target.checked)}
              className="h-5 w-5"
            />
            <span className="text-[13px] text-text-secondary">{t('create.acknowledge')}</span>
          </label>

          <div className="mt-auto flex flex-col gap-2">
            <Button
              variant="secondary"
              fullWidth
              onClick={copyMnemonic}
              disabled={copied}
            >
              {copied ? `✓ ${t('common.copied')}` : t('create.copyPhrase')}
            </Button>
            <Button
              variant="primary"
              fullWidth
              disabled={!acknowledged}
              onClick={() => setStep('verify')}
            >
              {t('create.continue')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  if (step === 'verify') {
    return (
      <div className="flex min-h-screen flex-col bg-bg px-5 pb-6 pt-3 text-text-primary">
        <CreateNav step={stepNumber()} onBack={handleBack} t={t} />
        <div className="mt-4 flex flex-1 flex-col gap-4">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[1.4px] text-text-tertiary">
              {t('create.verify2Label')}
            </div>
            <div className="mt-0.5 text-[15px] font-semibold text-text-primary">
              {t('create.tapThreeWords')}
            </div>
          </div>

          <div className="flex flex-col gap-2 rounded-xl border border-dashed border-border-strong bg-surface-1 p-3">
            {verifyPositions.map((pos, i) => {
              const filled = i < collected.length;
              const correct = filled && collected[i] === mnemonicWords[pos];
              return (
                <div key={pos} className="flex items-center gap-3">
                  <span className="min-w-[28px] font-mono text-[11px] text-text-tertiary">
                    #{pos + 1}
                  </span>
                  {filled ? (
                    <span
                      className={`rounded-lg px-3 py-1 font-mono text-[12px] ${
                        correct ? 'bg-text-primary text-bg' : 'bg-neg text-white'
                      }`}
                    >
                      {collected[i]}
                    </span>
                  ) : (
                    <span
                      className={`rounded-lg border border-dashed px-3 py-1 font-mono text-[12px] ${
                        i === nextSlotIndex
                          ? 'border-text-primary text-text-primary'
                          : 'border-text-disabled text-text-disabled'
                      }`}
                    >
                      ___
                    </span>
                  )}
                </div>
              );
            })}
          </div>

          {hasError ? (
            <div className="text-[11px] text-neg">
              {t('create.verifyError')}
            </div>
          ) : !verifyCorrect ? (
            <>
              <div className="text-[10px] font-semibold uppercase tracking-[1.4px] text-text-tertiary">
                {t('create.pickNextWord')}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {verifyPool.map((word, i) => (
                  <button
                    key={`${word}-${i}`}
                    type="button"
                    onClick={() => pickWord(word)}
                    className="rounded-lg bg-surface-2 px-3 py-1.5 font-mono text-[12px] text-text-primary hover:bg-surface-3 active:scale-[0.97]"
                  >
                    {word}
                  </button>
                ))}
              </div>
            </>
          ) : null}

          <div className="mt-auto flex flex-col gap-2">
            <Button
              variant="secondary"
              fullWidth
              onClick={resetVerify}
              disabled={collected.length === 0}
            >
              {t('create.clear')}
            </Button>
            {verifyCorrect && (
              <Button variant="primary" fullWidth onClick={() => setStep('pin')}>
                {t('create.continue')}
              </Button>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (step === 'pin') {
    return (
      <div className="flex min-h-screen flex-col bg-bg px-5 pb-6 pt-3 text-text-primary">
        <CreateNav step={stepNumber()} onBack={handleBack} t={t} />
        <div className="mt-4 flex flex-1 flex-col items-center justify-center gap-6">
          <div className="text-center">
            <div className="text-[10px] font-semibold uppercase tracking-[1.4px] text-text-tertiary">
              {t('create.pin')}
            </div>
            <div className="mt-3 text-[22px] font-semibold tracking-[-0.4px] text-text-primary">
              {t('create.createPin')}
            </div>
            <div className="mt-3 text-[14px] leading-relaxed text-text-secondary">
              {t('create.pinDescription')}
            </div>
          </div>

          <PinPad
            onComplete={(pin) => {
              setFirstPin(pin);
              setStep('confirm-pin');
            }}
          />
        </div>
      </div>
    );
  }

  return (
    <div className="flex min-h-screen flex-col bg-bg px-5 pb-6 pt-3 text-text-primary">
      <CreateNav step={stepNumber()} onBack={handleBack} t={t} />
      <div className="mt-4 flex flex-1 flex-col items-center justify-center gap-6">
        <div className="text-center">
          <div className="text-[10px] font-semibold uppercase tracking-[1.4px] text-text-tertiary">
            {t('create.pinConfirm')}
          </div>
          <div className="mt-3 text-[22px] font-semibold tracking-[-0.4px] text-text-primary">
            {t('create.enterAgain')}
          </div>
        </div>

        <PinPad
          onComplete={async (pin) => {
            if (pin !== firstPin) {
              setError(t('create.mismatchPin'));
              setStep('pin');
              setFirstPin(null);
              return;
            }
            await finalize(pin);
          }}
        />

        {error && (
          <div className="mt-2 text-center text-[11px] text-neg">{error}</div>
        )}

        {pendingOffer && (
          <BiometricOfferModal
            wallet={pendingOffer.wallet}
            derivedKey={pendingOffer.derivedKey}
            onDone={() => {
              setPendingOffer(null);
              navigate('/home');
            }}
          />
        )}
      </div>
    </div>
  );
}
